/**
 * SeatSnap Authentication JavaScript
 *
 * Client-side form validation and submission handling for login and signup pages.
 * Features:
 * - Form validation
 * - Password visibility toggle
 * - Password strength indicator
 * - Async form submission
 * - Error handling
 */

// ============================================================================
// Initialization
// ============================================================================

document.addEventListener('DOMContentLoaded', () => {
  const loginForm = document.querySelector('[data-auth-form="login"]');
  const signupForm = document.querySelector('[data-auth-form="signup"]');

  if (loginForm) {
    initLoginForm(loginForm);
  }

  if (signupForm) {
    initSignupForm(signupForm);
  }

  // Initialize password toggle for all forms
  initPasswordToggles();
});

// ============================================================================
// Login Form
// ============================================================================

function initLoginForm(form) {
  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    // Get form data
    const formData = new FormData(form);
    const email = formData.get('email');
    const password = formData.get('password');

    // Get inputs for error highlighting
    const emailInput = form.querySelector('input[name="email"]');
    const passwordInput = form.querySelector('input[name="password"]');

    // Client-side validation
    if (!email || !password) {
      const targetInput = !email ? emailInput : passwordInput;
      showError(form, 'Please fill in all fields', targetInput);
      return;
    }

    if (!isValidEmail(email)) {
      showError(form, 'Please enter a valid email address', emailInput);
      return;
    }

    // Show loading state
    setLoading(form, true);
    clearError(form);

    try {
      // Submit to API
      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      // Success - redirect to dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      setLoading(form, false);
      showError(form, error.message || 'An error occurred. Please try again.');
    }
  });
}

// ============================================================================
// Signup Form
// ============================================================================

function initSignupForm(form) {
  const passwordInput = form.querySelector('#password');
  const strengthIndicator = form.querySelector('.strength-fill');

  // Password strength indicator
  if (passwordInput && strengthIndicator) {
    passwordInput.addEventListener('input', () => {
      const strength = calculatePasswordStrength(passwordInput.value);
      updatePasswordStrength(strengthIndicator, strength);
      updatePasswordRequirements(form, passwordInput.value);
    });
  }

  // Form submission
  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    // Get form data
    const formData = new FormData(form);
    const name = formData.get('name');
    const email = formData.get('email');
    const password = formData.get('password');
    const terms = formData.get('terms');

    // Get inputs
    const nameInput = form.querySelector('input[name="name"]');
    const emailInput = form.querySelector('input[name="email"]');
    const termsInput = form.querySelector('input[name="terms"]');

    // Client-side validation
    if (!name || !email || !password) {
      let target = null;
      if (!name) target = nameInput;
      else if (!email) target = emailInput;
      else if (!password) target = passwordInput;

      showError(form, 'Please fill in all fields', target);
      return;
    }

    if (!isValidEmail(email)) {
      showError(form, 'Please enter a valid email address', emailInput);
      return;
    }

    if (password.length < 8) {
      showError(form, 'Password must be at least 8 characters', passwordInput);
      return;
    }

    if (!terms) {
      showError(form, 'You must accept the Terms of Service', termsInput);
      return;
    }

    // Show loading state
    setLoading(form, true);
    clearError(form);

    try {
      // Submit to API
      const response = await fetch('/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          email,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Signup failed');
      }

      // Success - redirect to dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      setLoading(form, false);
      showError(form, error.message || 'An error occurred. Please try again.');
    }
  });
}

// ============================================================================
// Password Toggle
// ============================================================================

function initPasswordToggles() {
  const toggleButtons = document.querySelectorAll('.toggle-password');

  toggleButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const wrapper = button.closest('.password-wrapper');
      const input = wrapper.querySelector('input[type="password"], input[type="text"]');

      if (input.type === 'password') {
        input.type = 'text';
        button.setAttribute('aria-label', 'Hide password');
      } else {
        input.type = 'password';
        button.setAttribute('aria-label', 'Show password');
      }
    });
  });
}

// ============================================================================
// Password Strength
// ============================================================================

function calculatePasswordStrength(password) {
  let strength = 0;

  if (password.length >= 8) strength++;
  if (password.length >= 12) strength++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
  if (/\d/.test(password)) strength++;
  if (/[^a-zA-Z0-9]/.test(password)) strength++;

  // Normalize to 0-3 scale
  if (strength <= 1) return 0;
  if (strength === 2) return 1;
  if (strength === 3) return 2;
  return 3;
}

function updatePasswordStrength(indicator, strength) {
  indicator.setAttribute('data-strength', strength);
}

function updatePasswordRequirements(form, password) {
  const requirements = form.querySelectorAll('[data-requirement]');

  requirements.forEach((req) => {
    const requirement = req.getAttribute('data-requirement');

    switch (requirement) {
      case 'length':
        if (password.length >= 8) {
          req.classList.add('met');
        } else {
          req.classList.remove('met');
        }
        break;
    }
  });
}

// ============================================================================
// Error Handling
// ============================================================================

function showError(form, message, input = null) {
  clearError(form);

  const errorDiv = document.createElement('div');
  errorDiv.className = 'error-message';
  errorDiv.setAttribute('role', 'alert');
  errorDiv.setAttribute('data-testid', 'error-message');
  errorDiv.innerHTML = `
    <svg class="error-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd"/>
    </svg>
    ${escapeHtml(message)}
  `;

  // Insert before the form
  form.parentNode.insertBefore(errorDiv, form);

  if (input) {
    input.setAttribute('aria-invalid', 'true');
    input.addEventListener(
      'input',
      () => {
        input.removeAttribute('aria-invalid');
      },
      { once: true }
    );
  }
}

function clearError(form) {
  const existingError = form.parentNode.querySelector('.error-message');
  if (existingError) {
    existingError.remove();
  }
}

function setLoading(form, isLoading) {
  const submitBtn = form.querySelector('button[type="submit"]');
  const spinnerDiv = form.querySelector('.loading-spinner');

  if (isLoading) {
    // Disable button
    submitBtn.disabled = true;

    // Save original content
    if (!submitBtn.dataset.originalContent) {
      submitBtn.dataset.originalContent = submitBtn.innerHTML;
    }

    // Inject spinner
    if (spinnerDiv) {
      // Clone spinner content
      const spinnerContent = spinnerDiv.innerHTML;
      submitBtn.innerHTML = spinnerContent;

      // Adjust colors for visibility on disabled gray background
      // Use dark gray text instead of white for better contrast on disabled button
      submitBtn.style.color = 'var(--color-gray-800)';
      submitBtn.style.opacity = '1'; // Force full opacity to ensure text is readable

      const svg = submitBtn.querySelector('svg');
      if (svg) {
        svg.style.color = 'currentColor';
        // Ensure classes are preserved or added if needed
        if (!svg.classList.contains('spinner')) {
          svg.classList.add('spinner');
        }
      }
    } else {
      submitBtn.textContent = 'Processing...';
      submitBtn.style.color = 'var(--color-gray-800)';
      submitBtn.style.opacity = '1';
    }

    form.classList.add('is-loading');
  } else {
    // Restore
    submitBtn.disabled = false;
    submitBtn.style.color = ''; // Restore original text color
    submitBtn.style.opacity = ''; // Restore original opacity
    if (submitBtn.dataset.originalContent) {
      submitBtn.innerHTML = submitBtn.dataset.originalContent;
    }
    form.classList.remove('is-loading');
  }
}

// ============================================================================
// Utilities
// ============================================================================

function isValidEmail(email) {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
